import { getAllAppointments } from "../services/appointmentService";
import { getOrganizationId } from "./organizationUtils";
import {
  APPOINTMENT_AUTO_CANCEL_GRACE_DAYS,
  autoCancelPastAppointments,
} from "./appointmentCancelUtils";

/** Local wall-clock hours: every 4 hours (… 8am, 12pm, 4pm, 8pm …). */
export const APPOINTMENT_AUTO_CANCEL_CRON_HOURS = [0, 4, 8, 12, 16, 20];

const STORAGE_PREFIX = "hms_appt_auto_cancel";
const LOCK_TTL_MS = 10 * 60 * 1000; // 10 minutes
const MIN_INTERVAL_MS = 60 * 1000; // avoid thrashing if tabs wake together

/** Shared across remounts so HMR / auth identity churn cannot re-fire immediately. */
let globalLastAutoCancelRunAt = 0;
let globalAutoCancelRunning = false;

const pad2 = (value) => String(value).padStart(2, "0");

export const getAutoCancelSlotKey = (date = new Date()) => {
  const d = date instanceof Date ? date : new Date(date);
  const hours = [...APPOINTMENT_AUTO_CANCEL_CRON_HOURS].sort((a, b) => a - b);
  let slotHour = hours[0];
  for (const hour of hours) {
    if (hour <= d.getHours()) slotHour = hour;
  }
  const dateKey = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  return `${dateKey}_${pad2(slotHour)}`;
};

export const getNextAutoCancelRunAt = (now = new Date()) => {
  const current = now instanceof Date ? now : new Date(now);
  const hours = [...APPOINTMENT_AUTO_CANCEL_CRON_HOURS].sort((a, b) => a - b);

  for (const hour of hours) {
    const candidate = new Date(
      current.getFullYear(),
      current.getMonth(),
      current.getDate(),
      hour,
      0,
      0,
      0
    );
    if (candidate.getTime() > current.getTime()) {
      return candidate;
    }
  }

  const tomorrowFirst = new Date(
    current.getFullYear(),
    current.getMonth(),
    current.getDate() + 1,
    hours[0],
    0,
    0,
    0
  );
  return tomorrowFirst;
};

const storageKey = (organizationId, slotKey) =>
  `${STORAGE_PREFIX}:${organizationId || "org"}:${slotKey}`;

const readJson = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const writeJson = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore quota / private mode
  }
};

const tryAcquireSlotLock = (organizationId, slotKey, now = Date.now()) => {
  const key = storageKey(organizationId, slotKey);
  const existing = readJson(key);
  if (existing?.status === "done") {
    return { acquired: false, reason: "already-done" };
  }
  if (
    existing?.status === "running" &&
    existing?.startedAt &&
    now - Number(existing.startedAt) < LOCK_TTL_MS
  ) {
    return { acquired: false, reason: "locked" };
  }

  const token = `${now}-${Math.random().toString(36).slice(2, 10)}`;
  writeJson(key, { status: "running", startedAt: now, token });
  const verify = readJson(key);
  if (verify?.token !== token) {
    return { acquired: false, reason: "lost-race" };
  }
  return { acquired: true, key, token };
};

const markSlotDone = (key, token, result) => {
  const existing = readJson(key);
  if (existing?.token && existing.token !== token) return;
  writeJson(key, {
    status: "done",
    finishedAt: Date.now(),
    token,
    cancelled: result?.cancelled ?? 0,
    attempted: result?.attempted ?? 0,
  });
};

const releaseSlotLock = (key, token) => {
  const existing = readJson(key);
  if (existing?.token && existing.token !== token) return;
  if (existing?.status === "running") {
    try {
      localStorage.removeItem(key);
    } catch {
      // ignore
    }
  }
};

/**
 * Fetch org appointments and auto-cancel past incomplete / no-show visits.
 * Safe to call from any authenticated staff session (role-agnostic).
 */
export const runAppointmentAutoCancelJob = async ({
  organizationId = getOrganizationId(null),
  graceDays = APPOINTMENT_AUTO_CANCEL_GRACE_DAYS,
  now = new Date(),
  force = false,
} = {}) => {
  const slotKey = getAutoCancelSlotKey(now);
  if (!force) {
    const lock = tryAcquireSlotLock(organizationId, slotKey, now.getTime());
    if (!lock.acquired) {
      return {
        skipped: true,
        reason: lock.reason,
        slotKey,
        cancelled: 0,
      };
    }

    try {
      const appointments = await getAllAppointments();
      const result = await autoCancelPastAppointments(appointments, {
        graceDays,
        now,
      });
      markSlotDone(lock.key, lock.token, result);
      return {
        skipped: false,
        slotKey,
        cancelled: result.cancelled || 0,
        attempted: result.attempted || 0,
      };
    } catch (error) {
      releaseSlotLock(lock.key, lock.token);
      throw error;
    }
  }

  const appointments = await getAllAppointments();
  const result = await autoCancelPastAppointments(appointments, {
    graceDays,
    now,
  });
  return {
    skipped: false,
    slotKey,
    cancelled: result.cancelled || 0,
    attempted: result.attempted || 0,
  };
};

/**
 * Starts an in-app cron: run for the current slot (if due), then schedule
 * future runs at 8:00 / 12:00 / 16:00 / 20:00 / 00:00 / 04:00 local time.
 * Returns a dispose function.
 */
export const startAppointmentAutoCancelScheduler = ({
  organizationId = getOrganizationId(null),
  graceDays = APPOINTMENT_AUTO_CANCEL_GRACE_DAYS,
  onResult,
  onError,
} = {}) => {
  let disposed = false;
  let timeoutId = null;

  const execute = async (reason = "scheduled") => {
    if (disposed || globalAutoCancelRunning) return;
    const nowMs = Date.now();
    if (nowMs - globalLastAutoCancelRunAt < MIN_INTERVAL_MS) return;
    globalAutoCancelRunning = true;
    globalLastAutoCancelRunAt = nowMs;
    try {
      const result = await runAppointmentAutoCancelJob({
        organizationId,
        graceDays,
        now: new Date(),
      });
      if (result?.cancelled > 0) {
        console.info(
          `[AppointmentAutoCancel] ${reason}: cancelled ${result.cancelled} appointment(s) (slot ${result.slotKey})`
        );
      } else if (!result?.skipped) {
        console.info(
          `[AppointmentAutoCancel] ${reason}: no past incomplete appointments (slot ${result.slotKey})`
        );
      }
      onResult?.(result);
    } catch (error) {
      console.error("[AppointmentAutoCancel] Scheduler run failed:", error);
      onError?.(error);
    } finally {
      globalAutoCancelRunning = false;
    }
  };

  const scheduleNext = () => {
    if (disposed) return;
    if (timeoutId) clearTimeout(timeoutId);
    const nextAt = getNextAutoCancelRunAt(new Date());
    const delay = Math.max(nextAt.getTime() - Date.now(), MIN_INTERVAL_MS);
    timeoutId = setTimeout(async () => {
      await execute("cron");
      scheduleNext();
    }, delay);
  };

  // Catch up current slot on boot (e.g. app opened at 9am → still run 8am slot once).
  execute("startup");
  scheduleNext();

  const onVisibility = () => {
    if (document.visibilityState === "visible") {
      execute("visibility");
    }
  };
  document.addEventListener("visibilitychange", onVisibility);

  return () => {
    disposed = true;
    if (timeoutId) clearTimeout(timeoutId);
    document.removeEventListener("visibilitychange", onVisibility);
  };
};
